// DOM rendering for board + bar. Pure-ish: given a `GameState` and root
// elements, sync the DOM to reflect that state. No game logic lives here.
//
// Strategy: for Phase 2 we rebuild the board's 49 cells and the bar's slots
// on every render. This is simple, correct, and fast (the board is tiny).
// Phase 5+ can introduce diffing if profiling justifies it.
//
// TODO Phase 7 a11y audit: verify the grid/row/cell ARIA tree exposes
// correctly on iOS Safari 17+ VoiceOver and on NVDA + Chrome.

import type { Puzzle } from './puzzle'
import { computeSlotStates } from './slotStates'
import { BOARD_SIZE, encode, type EncodedCell, type GameState, type TokenIdx } from './state'

const UNDO_ARROW_GLYPH = '↩️'  // undo arrow (U+21A9 U+FE0F)
const UNDO_SPENT_GLYPH = '\u{1F7E7}'  // 🟧 large orange square (U+1F7E7)

// View-model for the undo button. The controller computes this from its
// undoStack + state on every render; the render function is pure.
export interface UndoButtonModel {
  readonly undoCount: number
  readonly undoStackDepth: number
  readonly maxUndos: number
  readonly canUndo: boolean
}

export interface RenderOptions {
  // If set, after rendering, focus the cell currently occupied by this
  // token index. Used after a flick so keyboard users stay on a moving tile.
  readonly focusTokenIdx?: number
}

export function renderBoard(
  boardRoot: HTMLElement,
  state: GameState,
  options: RenderOptions = {},
): void {
  boardRoot.replaceChildren()

  // Build a reverse map for cell -> letter so we render with a single pass.
  const cellLetter = new Map<EncodedCell, string>()
  for (const [cell, tokenIdx] of state.cellToToken) {
    const letter = state.letters[tokenIdx]
    if (letter === undefined) {
      throw new Error(`Invariant: no letter for tokenIdx ${tokenIdx}`)
    }
    cellLetter.set(cell, letter)
  }

  // Where to place focus after this render, if any.
  let focusCell: EncodedCell | null = null
  if (options.focusTokenIdx !== undefined) {
    const cell = state.tokenToCell.get(options.focusTokenIdx)
    if (cell !== undefined) {
      focusCell = cell
    }
  }
  let focusTarget: HTMLElement | null = null

  // Phase 3: when the game is over, filled cells are made non-focusable and
  // announce themselves as disabled. The controller also short-circuits any
  // pointer/keyboard input via a status guard in dispatchAndRender, so this
  // is belt-and-suspenders for assistive tech.
  const isLocked = state.status !== 'playing'

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      const cellEncoded = encode(row, col)
      const cell = document.createElement('div')
      cell.className = 'cell'
      cell.dataset['cell'] = String(cellEncoded)

      const humanRow = row + 1
      const humanCol = col + 1

      const letter = cellLetter.get(cellEncoded)
      if (letter === undefined) {
        cell.setAttribute('aria-hidden', 'true')
        cell.setAttribute('tabindex', '-1')
      } else {
        cell.dataset['filled'] = 'true'
        // Phase 7: data-token-idx lets the cascade animator look up DOM
        // elements by tokenIdx without re-scanning the board on every step.
        // Built into a Map by buildTokenDomLookup() BEFORE the controller
        // re-renders post-flick, so the lookup points at pre-flick positions.
        const tokenIdx = state.cellToToken.get(cellEncoded)
        if (tokenIdx !== undefined) {
          cell.dataset['tokenIdx'] = String(tokenIdx)
        }
        cell.textContent = letter
        cell.setAttribute('role', 'gridcell')
        if (isLocked) {
          cell.setAttribute('tabindex', '-1')
          cell.setAttribute('aria-disabled', 'true')
        } else {
          cell.setAttribute('tabindex', '0')
        }
        cell.setAttribute(
          'aria-label',
          `${letter.toUpperCase()}, row ${humanRow} column ${humanCol}`,
        )
        if (!isLocked && cellEncoded === focusCell) {
          focusTarget = cell
        }
      }

      boardRoot.appendChild(cell)
    }
  }

  if (focusTarget !== null) {
    focusTarget.focus()
  }
}

// Render the game-over banner: a real <button> overlaying the board's bottom
// rows once the game ends. It replaces the old status line + the separate
// "View results" button with one tappable affordance that both announces the
// terminal state and reopens the endgame modal.
//
// Visibility split: this renderer owns the TEXT and the hide-during-play; it
// never unhides. Every terminal transition auto-opens the endgame modal, and
// the banner's reveal is deferred until the player dismisses that initial
// modal — handleEndgameClose in main.ts is the single place hidden is cleared.
// (Showing it earlier was pointless behind the modal and stole board real
// estate during the win/loss animation moment.)
//
// a11y: the button lives inside an aria-live=polite host so the win/lose
// announcement (incl. the revealed word on a loss) is read out when it's
// revealed on modal close. It's a real <button>, so it's keyboard-focusable
// and fires on Enter/Space for free. While playing it's hidden via the
// `hidden` attribute, which removes it from the tab order and the
// accessibility tree (gameOverBanner.css re-asserts [hidden] { display: none }
// because its own display: block would otherwise override the UA rule).
export function renderGameOverBanner(
  bannerButton: HTMLButtonElement,
  state: GameState,
  targetWord: string,
): void {
  switch (state.status) {
    case 'playing':
      // Hidden + cleared during normal play: no overlay, nothing announced,
      // not in the tab order.
      bannerButton.hidden = true
      bannerButton.textContent = ''
      delete bannerButton.dataset['outcome']
      return
    case 'won':
      // The \n becomes a real line break via white-space: pre-line on
      // .gameover-banner — the affordance reads on its own line.
      // data-outcome drives the border color (victory blue vs loss orange) in
      // gameOverBanner.css.
      bannerButton.textContent = 'Solved!\nTap for results.'
      bannerButton.dataset['outcome'] = 'won'
      return
    case 'lost':
      // Preserves the prior loss copy (reveals the target word) and appends the
      // tap-for-results affordance now that the banner is the reopen control.
      bannerButton.textContent = `Out of undos. The word was ${targetWord.toUpperCase()}.\nTap for results.`
      bannerButton.dataset['outcome'] = 'lost'
      return
  }
}

// Render the undo button. The label is the literal word "Undo" (left in
// markup) plus a 6-slot emoji row whose textContent we sync here: each
// unused undo is an undo-arrow glyph, each consumed undo is an orange
// square, with squares growing from the right. The whole element remains
// one tap target; the inner spans are aria-hidden and the accessible name
// is set via aria-label.
export function renderUndoButton(
  undoRoot: HTMLButtonElement,
  model: UndoButtonModel,
): void {
  const remaining = Math.max(0, model.maxUndos - model.undoCount)
  const spent = model.maxUndos - remaining
  const glyphs: string[] = []
  for (let i = 0; i < remaining; i += 1) glyphs.push(UNDO_ARROW_GLYPH)
  for (let i = 0; i < spent; i += 1) glyphs.push(UNDO_SPENT_GLYPH)
  const joined = glyphs.join(' ')

  const slots = undoRoot.querySelector('.undo-row__slots')
  if (slots !== null) {
    slots.textContent = joined
  } else {
    // Defensive fallback: markup is missing the inner span. Render the row
    // directly on the button so the player still sees state.
    undoRoot.textContent = joined
  }

  undoRoot.disabled = !model.canUndo
  undoRoot.setAttribute(
    'aria-label',
    `Undo. Press U to undo. ${String(remaining)} of ${String(model.maxUndos)} remaining.`,
  )
  undoRoot.setAttribute(
    'title',
    `Undo (${String(remaining)} of ${String(model.maxUndos)} remaining). Shortcut: U`,
  )
}

// View-model for the subtitle line between the page title and the board.
// Both number and date come from the puzzle's curator-assigned data — the
// subtitle is the data, not derived from the wall clock — so the same puzzle
// reads the same subtitle on every mount path (daily, dev-next, archive).
export interface PuzzleSubtitleModel {
  readonly puzzleNumber: number
  // Curator-assigned ISO date string (`YYYY-MM-DD`) on the puzzle entry
  // (`letter_flick_puzzle_date`). Always present when a puzzle is being shown.
  readonly puzzleDate: string
  readonly label?: string
}

// Render the subtitle: "Letter Flick #1 · May 25, 2026". Uses textContent so
// untrusted strings can't inject markup. Locale formatting via
// Intl.DateTimeFormat with the player's default locale.
export function renderPuzzleSubtitle(
  subtitleRoot: HTMLElement,
  model: PuzzleSubtitleModel,
): void {
  const label = model.label !== undefined ? model.label : 'Letter Flick'
  const dateLabel = formatLongLocalDate(model.puzzleDate)
  subtitleRoot.textContent = `${label} #${String(model.puzzleNumber)} · ${dateLabel}`
}

// Parse 'YYYY-MM-DD' as a local-midnight Date and format it with the
// player's locale. `new Date(s)` is avoided — Date.parse treats bare
// 'YYYY-MM-DD' as UTC in most engines, which can skew across timezones.
function formatLongLocalDate(iso: string): string {
  const y = Number(iso.slice(0, 4))
  const m = Number(iso.slice(5, 7))
  const d = Number(iso.slice(8, 10))
  const dt = new Date(y, m - 1, d)
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dt)
}

export function renderBar(
  barRoot: HTMLElement,
  state: GameState,
  puzzle: Puzzle,
): void {
  barRoot.style.setProperty('--bar-slot-count', String(state.wordLength))

  // Reuse existing slot elements when the count matches. The ::after pseudo
  // on each .slot runs a 6.4s shine animation whose clock is anchored to
  // the element's creation time — destroying and recreating slots on every
  // render (the prior strategy) reset all 10 animations to t=0 each flick,
  // visibly restarting the rolling-wave effect. Reusing preserves the
  // animation phase across renders so the wave keeps rolling smoothly.
  if (barRoot.children.length !== state.wordLength) {
    barRoot.replaceChildren()
    for (let i = 0; i < state.wordLength; i += 1) {
      const slot = document.createElement('li')
      slot.className = 'slot'
      barRoot.appendChild(slot)
    }
  }

  // Slot classification lives in slotStates.ts (the single gated source: DAG
  // walk when live, per-slot union otherwise — §4.1, §11). See that file for
  // the three-way rationale and the iOS-vs-web divergence note.
  const slotStates = computeSlotStates(state, puzzle)
  for (let i = 0; i < state.wordLength; i += 1) {
    const slot = barRoot.children[i]
    if (!(slot instanceof HTMLLIElement)) continue

    // Reset to a clean baseline before applying the new state. Slot reuse
    // means stale attributes from the previous render must be explicitly
    // cleared — otherwise an undo (filled → empty) would leave data-state
    // and data-filled set on the now-empty slot.
    delete slot.dataset['state']
    delete slot.dataset['filled']
    slot.textContent = ''

    const letter = state.exitedLetters[i]
    const humanSlot = i + 1
    if (letter === undefined) {
      slot.setAttribute('aria-label', `Empty slot ${humanSlot} of ${state.wordLength}`)
      continue
    }
    slot.dataset['filled'] = 'true'
    slot.textContent = letter
    const slotState = slotStates[i]
    // letter !== undefined above means this slot is filled, so the gated source
    // never returns 'empty' here. The three remaining values match the
    // data-state contract exactly.
    if (slotState === 'correct-canonical') {
      slot.dataset['state'] = 'correct-canonical'
      slot.setAttribute(
        'aria-label',
        `${letter.toUpperCase()} in slot ${humanSlot} of ${state.wordLength}, correct`,
      )
    } else if (slotState === 'correct-alt') {
      slot.dataset['state'] = 'correct-alt'
      slot.setAttribute(
        'aria-label',
        `${letter.toUpperCase()} in slot ${humanSlot} of ${state.wordLength}, correct via alternative path`,
      )
    } else if (slotState === 'incorrect') {
      slot.dataset['state'] = 'incorrect'
      slot.setAttribute(
        'aria-label',
        `${letter.toUpperCase()} in slot ${humanSlot} of ${state.wordLength}, incorrect`,
      )
    }
  }
}

// Phase 7: walk the board's filled cells and build a tokenIdx -> element
// map. Used by the controller to hand the cascade animator stable DOM refs
// for the duration of the animation (the canonical re-render at onComplete
// throws all these refs away).
//
// Called BEFORE the post-flick re-render — at that point the board DOM still
// reflects the pre-flick GameState, so a cell's data-token-idx still maps
// to the cell it visually occupied before the cascade started. That's
// precisely what the animator needs as its slide-from anchor.
//
// Returns a fresh Map; the caller is free to discard it after onComplete.
export function buildTokenDomLookup(
  boardRoot: HTMLElement,
): Map<TokenIdx, HTMLElement> {
  const lookup = new Map<TokenIdx, HTMLElement>()
  const cells = boardRoot.querySelectorAll<HTMLElement>('.cell[data-token-idx]')
  cells.forEach((cell) => {
    const raw = cell.dataset['tokenIdx']
    if (raw === undefined) return
    const n = Number(raw)
    if (!Number.isFinite(n)) return
    lookup.set(n, cell)
  })
  return lookup
}

// Measure the pixel side-length of a single board cell from the live DOM.
// Returns null if the board has no laid-out width yet (zero / not in DOM /
// not yet styled). The board is a CSS grid with 7 equal columns, so dividing
// the board's content-box width by BOARD_SIZE yields the cell size that the
// animator's translate distances need to match.
export function measureCellSizePx(boardRoot: HTMLElement): number | null {
  const rect = boardRoot.getBoundingClientRect()
  if (rect.width <= 0) return null
  return rect.width / BOARD_SIZE
}

// Trigger a brief shake animation on the cell at `cellEncoded`. No-op if the
// cell isn't present in the current render. Used to signal an illegal flick.
// The animation is gated by `prefers-reduced-motion` in CSS.
export function shakeCell(boardRoot: HTMLElement, cellEncoded: EncodedCell): void {
  const cell = boardRoot.querySelector<HTMLElement>(
    `.cell[data-cell="${String(cellEncoded)}"]`,
  )
  if (cell === null) return
  // Re-trigger animation if the class is already present.
  cell.classList.remove('cell--shake')
  // Force reflow so the next class-add restarts the animation.
  void cell.offsetWidth
  cell.classList.add('cell--shake')
  cell.addEventListener(
    'animationend',
    () => cell.classList.remove('cell--shake'),
    { once: true },
  )
}

